import {ActivityIndicator, StyleSheet, TouchableOpacity} from 'react-native';
import {Box, HBox} from '../../components/core/view';
import {
  CustomText,
  H3Text,
  H6Text,
  PSmallText,
} from '../../components/core/text';
import {OutreachShift, OutreachStatus} from '../types/shiftClaim';
import {
  formatShiftTime,
  isReleaseAllowed,
  parseStoreName,
} from '../utils/formatShiftTime';

import If from '@buncha/components/conditional/If';
import React from 'react';
import ShopIcon from '@buncha/components/core/icon/ShopIcon';
import {ToastType} from '@buncha/components/composites/notification/type';
import font from '@buncha/appStyles/style/font';
import {getWidth} from '@buncha/utils/common';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {useToastMessage} from '@buncha/components/composites/notification';

interface ShiftClaimCardProps {
  shift: OutreachShift;
  isClaimed: boolean;
  isLoading: boolean;
  onClaim?: (shiftId: string) => void;
  onPassPress?: (shift: OutreachShift) => void;
  onReleasePress?: (shift: OutreachShift) => void;
}

const ShiftClaimCard: React.FC<ShiftClaimCardProps> = ({
  shift,
  isClaimed,
  isLoading,
  onClaim,
  onPassPress,
  onReleasePress,
}) => {
  const storeName = parseStoreName(shift.store_name);
  const theme = useColorThemeType('main');
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const shiftDate = shift.shopper_shift_start
    ? new Date(shift.shopper_shift_start)
        .toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
        .toUpperCase()
    : '';

  const shiftTime =
    shift.shopper_shift_start && shift.driver_shift_end
      ? `${formatShiftTime(shift.shopper_shift_start)} – ${formatShiftTime(
          shift.driver_shift_end,
        )}`
      : '';

  const showClaimed = isClaimed || shift.status === OutreachStatus.Accepted;
  const canRelease = isReleaseAllowed(shift.shopper_shift_start);

  if (showClaimed)
    return (
      <Box
        style={[
          styles.claimedCard,
          {
            backgroundColor: theme?.background.green[300],
            borderColor: theme?.background.green[100],
          },
        ]}>
        <HBox style={styles.claimedHeaderRow}>
          <Box style={styles.dateBlock}>
            <H6Text style={{color: theme?.text.blue[300]}}>{shiftDate}</H6Text>
            <H3Text style={[styles.timeText, {color: theme?.text.blue[300]}]}>
              {shiftTime}
            </H3Text>
            <HBox>
              <ShopIcon
                width={Number(getWidth(15))}
                fill={theme?.text.gray[100]}
              />
              <PSmallText style={styles.storeName}>{storeName}</PSmallText>
            </HBox>
          </Box>
          <Box
            style={[
              styles.badge,
              {backgroundColor: theme?.background.green[100]},
            ]}>
            <H6Text style={[styles.badgeText, {color: theme?.text.white[100]}]}>
              ✓ Claimed
            </H6Text>
          </Box>
        </HBox>
        <Box style={styles.releaseRow}>
          <TouchableOpacity
            onPress={() =>
              isReleaseAllowed(shift.shopper_shift_start)
                ? onReleasePress?.(shift)
                : showErrorToast(
                    'Shifts cannot be released within 1 hour of their start time.',
                  )
            }
            activeOpacity={0.7}>
            <CustomText
              style={[
                canRelease ? styles.releaseText : styles.lockedText,
                {
                  color: canRelease
                    ? theme?.text.blue[100]
                    : theme?.text.gray[100],
                },
              ]}>
              {canRelease
                ? 'Release this shift'
                : 'Cannot release within 1 hr of start'}
            </CustomText>
          </TouchableOpacity>
        </Box>
      </Box>
    );

  return (
    <Box
      style={[
        styles.availableCard,
        {backgroundColor: theme?.background?.white[100]},
      ]}>
      <Box style={styles.dateBlock}>
        <H6Text style={{color: theme?.text.blue[300]}}>{shiftDate}</H6Text>
        <H3Text
          style={[
            styles.timeText,
            {
              color: theme?.text.blue[300],
              fontFamily: font.fontTheme.muli[700].normal,
            },
          ]}>
          {shiftTime}
        </H3Text>
        <HBox>
          <ShopIcon width={Number(getWidth(15))} fill={theme?.text.gray[100]} />
          <PSmallText
            style={[styles.storeName, {color: theme?.text.gray[100]}]}>
            {storeName}
          </PSmallText>
        </HBox>
      </Box>
      <HBox style={styles.actionRow}>
        <TouchableOpacity
          style={[styles.outlineBtn, {borderColor: theme?.text.gray[100]}]}
          onPress={() => onPassPress?.(shift)}
          activeOpacity={0.7}>
          <H6Text
            style={[styles.outlineBtnText, {color: theme?.text.blue[300]}]}>
            Not this one
          </H6Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.solidBtn,
            {backgroundColor: theme?.background.blue[300]},
          ]}
          onPress={() => onClaim?.(shift.id)}
          disabled={isLoading}
          activeOpacity={0.8}>
          <If condition={isLoading}>
            <ActivityIndicator color={theme?.text.white[100]} size="small" />
          </If>
          <If condition={!isLoading}>
            <H6Text
              style={[styles.solidBtnText, {color: theme?.text.white[100]}]}>
              ✓ Claim
            </H6Text>
          </If>
        </TouchableOpacity>
      </HBox>
    </Box>
  );
};

const styles = StyleSheet.create({
  badge: {
    borderRadius: 20,
    paddingVertical: 4,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    height: 26,
  },
  badgeText: {
    fontWeight: '700',
  },
  claimedCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
  },
  claimedHeaderRow: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  dateBlock: {
    flex: 1,
    marginBottom: 2,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0,
  },
  timeText: {
    fontWeight: '800',
  },
  storeName: {
    fontWeight: '600',
    marginBottom: 4,
    marginLeft: 4,
  },
  releaseRow: {
    alignItems: 'flex-end',
    marginTop: -8,
  },
  releaseText: {
    fontWeight: '600',
    textDecorationLine: 'underline',
    textAlign: 'right',
  },
  lockedText: {
    textAlign: 'right',
    fontWeight: '500',
  },
  availableCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  actionRow: {
    marginTop: 16,
    gap: 10,
  },
  outlineBtn: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  outlineBtnText: {
    fontWeight: '600',
  },
  solidBtn: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  solidBtnText: {
    fontWeight: '700',
  },
});

export default React.memo(ShiftClaimCard);
